import fscreen from 'fscreen'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

export interface FullScreenHandle {
  active: boolean
  enter: () => void
  exit: () => void
  node?: React.MutableRefObject<HTMLDivElement | null>
}

/**
 * Full-screen controls for the current page.
 * Refactored from https://github.com/snakesilk/react-fullscreen.
 */

export const useFullScreenHandle = (): FullScreenHandle => {
  const documentEl = document.documentElement

  const [active, setActive] = useState<boolean>(false)
  const node = useRef<HTMLDivElement | null>(null)
  const el = node.current || documentEl

  useEffect(() => {
    const handleChange = () => {
      setActive(fscreen.fullscreenElement === el)
    }
    fscreen.addEventListener('fullscreenchange', handleChange)
    return () =>
      fscreen.removeEventListener('fullscreenchange', handleChange)
  }, [el])

  const enter = useCallback(() => {
    if (el) {
      return fscreen.requestFullscreen(el)
    }
  }, [el])

  const exit = useCallback(() => {
    if (fscreen.fullscreenElement === el) {
      return fscreen.exitFullscreen()
    }
  }, [el])

  return useMemo(
    () => ({
      active,
      enter,
      exit,
      node
    }),
    [active, enter, exit]
  )
}
